import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import CustomerLoginForm from './CustomerLoginForm';
import PointPurchaseModal from './PointPurchaseModal';
import ProductThumbnail from '../products/ProductThumbnail';
import CoinMark from './CoinMark';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import customerService from '../../services/customerService';
import { formatCurrency } from '../../utils/currency';
import { EntitlementResolution, PurchasableProduct } from '../../types/customer';
import { saveBookingResume } from '../../utils/bookingResume';

/**
 * The entitlement guard on a service booking page.
 *
 * SELECTION IS NEVER BLOCKED. Someone picks the service they want first, and
 * only then is told they need points for it. Disabling the list up front would
 * leave a visitor looking at options they cannot touch, with no idea what any
 * of it involves.
 *
 * The block happens when they try to MOVE ON, which is also the moment they are
 * most willing to buy: they have chosen something specific.
 *
 * IT SITS ABOVE BOTH STEPS, not inside the service list. A duration-mode page
 * has no service list — that step is skipped outright — so a guard living
 * inside it never rendered there at all, and a booking priced in points offered
 * no sign-in, no balance and no way to buy any.
 *
 * POINTS ONLY, NOW. A plan used to be blocked here too, which made picking the
 * plan option raise a sign-in dialog before the visitor had seen a single
 * available time. Subscribing happens at the END of the booking instead; this
 * guard is for points, which are prepaid and genuinely cannot be settled later.
 *
 * It reports through `onBlockedChange` rather than owning the flow — the flow
 * decides what "proceed" means; this only answers "may they?".
 */

interface ServiceEntitlementGuardProps {
  meetingTypeId?: number | null;
  organizationId?: number | null;
  /**
   * The host whose shop sells the points. Without it the "buy points" route
   * has nowhere to go, so the pop-up falls back to buying in place.
   */
  username?: string;
  accessRequirement: string;
  /** Cost depends on which service they picked, so it is re-resolved per pick. */
  selectedProductId: number | null;
  /**
   * The duration being booked. A page priced in POINTS prices per duration just
   * as a money one does, so omitting it resolved the cost to zero — which reads
   * as unbookable — and told a customer with a full wallet they were short.
   */
  durationMinutes?: number | null;
  /**
   * Whether this selection is stopped, and WHICH selection the answer is about.
   *
   * The product id matters because resolving is asynchronous: without it the
   * flow cannot tell "not blocked, we checked" from "not blocked, we have not
   * checked yet", and a points option would sail through to host selection on
   * the previous option's answer.
   */
  onBlockedChange: (blocked: boolean, resolvedForProductId: number | null) => void;
  /**
   * True when a PLAN covers this booking, so the caller can stop showing
   * prices. NOT the same as "the page mentions plans" — someone signed in
   * without a plan is charged like anyone else.
   *
   * Points do NOT count as covered, however healthy the balance. A plan means
   * nothing further is owed and a price on screen reads as a second charge;
   * points are a price, just denominated differently, and hiding it left
   * someone about to spend 5 of them with no idea it would cost anything.
   */
  onCoveredChange?: (
    covered: boolean,
    /**
     * How much of the plan's allowance is left, when a plan is what covers it.
     * The option list shows this INSTEAD of a price: someone already on the
     * plan is not deciding whether to buy it, they are deciding whether they
     * have a booking left.
     */
    usage?: { used: number; total: number | null }
  ) => void;
  /**
   * Set when the CHOSEN option cannot be paid for in money — a plan, or
   * something priced in points. An unmet entitlement really does stop those.
   */
  requiresEntitlement?: boolean;
  /** Shapes the message: subscribing and buying points are different acts. */
  selectedProductKind?: string;
  /**
   * Incremented by the caller each time someone tries to move on while
   * blocked. Opening is the caller's decision because only it knows whether a
   * booking was actually attempted.
   */
  promptToken?: number;
  /**
   * Fired once a sign-in has been resolved, so the flow can put them back at
   * the option list.
   *
   * Signing in changes what they can afford, and the options are where that
   * shows. Leaving them on whichever step the dialog happened to open over
   * means the first thing they see after signing in is a screen that does not
   * reflect what just changed.
   */
  onIdentityChanged?: () => void;
  /**
   * Whether the "already have points or a plan?" prompt belongs on the step
   * currently showing.
   *
   * It belongs at the start, where someone is deciding what to book. Carried
   * onward it followed them into host selection and sat above a screen it has
   * nothing to do with — an invitation to sign in for a choice they had already
   * made. The MODALS are unaffected: being stopped still explains itself
   * wherever it happens.
   */
  showSignInBanner?: boolean;
  /** Padding for the banner block, supplied by whichever step is showing. */
  bannerClassName?: string;
  /**
   * Fired when the pop-up is DISMISSED without being resolved — closed, or
   * cancelled — so the caller can release whatever selection led to it.
   *
   * Not fired when they leave through a door: picking a pack to buy, or
   * signing in. Those carry on with the option they chose, and dropping it
   * underneath them would undo the very thing they just did about it.
   */
  onDismissed?: () => void;
  /**
   * Raise the dialog as soon as this page is known to be unbookable, without
   * waiting for the visitor to try something.
   *
   * For a NON-SERVICE page, which has no option list to be stopped at. On a
   * service page the block belongs at the moment someone picks an option they
   * cannot afford; here there is nothing to pick, so the earliest honest
   * moment is the page itself. Fires ONCE — dismissing it must not summon it
   * again on the next resolve.
   */
  autoPromptWhenBlocked?: boolean;
  /**
   * The signed-in HOST is looking at their own booking page.
   *
   * Turns on a warning only they can see: a page priced in points with no
   * point pack on sale cannot be booked by anybody, and the host has no other
   * way to discover that — the guest-facing copy tells the GUEST to contact
   * the host, which is no use to the host reading their own link.
   */
  isPageOwner?: boolean;
}

const ServiceEntitlementGuard: React.FC<ServiceEntitlementGuardProps> = ({
  meetingTypeId,
  organizationId,
  username,
  accessRequirement,
  selectedProductId,
  durationMinutes,
  onBlockedChange,
  onCoveredChange,
  requiresEntitlement = false,
  selectedProductKind,
  promptToken = 0,
  onIdentityChanged,
  showSignInBanner = true,
  bannerClassName = '',
  onDismissed,
  autoPromptWhenBlocked = false,
  isPageOwner = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSignedIn, refresh } = useCustomerAuth();

  const [resolution, setResolution] = useState<EntitlementResolution | null>(null);
  const [products, setProducts] = useState<PurchasableProduct[]>([]);
  const [loginOpen, setLoginOpen] = useState(false);
  const [needOpen, setNeedOpen] = useState(false);
  const [buying, setBuying] = useState<PurchasableProduct | null>(null);
  /**
   * What the page asks for, straight from the purchasable read. Kept because
   * `accessRequirement` alone does not say it: a page priced in points at the
   * MEETING TYPE level leaves that column 'none' and is promoted server-side.
   */
  const [pointCost, setPointCost] = useState<number | null>(null);
  /**
   * Whether the "what is on sale" read has actually come back.
   *
   * An empty `products` means "nothing on sale" only AFTER this is true.
   * Before it, the list is empty because nothing has loaded — and the owner
   * warning below would tell a host with a perfectly good point pack that
   * their page is broken, every time they opened it.
   */
  const [purchasableLoaded, setPurchasableLoaded] = useState(false);
  /** Both dialogs below open once and stay closed once dismissed. */
  const autoPromptedRef = useRef(false);
  const [ownerWarningOpen, setOwnerWarningOpen] = useState(false);
  const ownerWarnedRef = useRef(false);

  const locked = accessRequirement !== 'none';

  const resolve = useCallback(async () => {
    if (!locked || !meetingTypeId) {
      onBlockedChange(false, selectedProductId);
      onCoveredChange?.(false);
      return;
    }
    try {
      const result = await customerService.getEntitlement(
        meetingTypeId,
        selectedProductId,
        durationMinutes
      );
      setResolution(result);
      /**
       * AN ENTITLEMENT IS AN ALTERNATIVE TO PAYING, NOT A PRECONDITION.
       *
       * Not holding a plan does not close the page — it means the ordinary
       * price applies. The only thing that genuinely stops a booking is a page
       * priced in POINTS, where there is no money price to charge instead.
       */
      onBlockedChange(requiresEntitlement && !result.allowed, selectedProductId);
      onCoveredChange?.(
        result.allowed === true && result.method === 'subscription',
        result.method === 'subscription'
          ? { used: result.allowanceUsed ?? 0, total: result.allowanceTotal ?? null }
          : undefined
      );
    } catch {
      // Fail OPEN here: the server re-checks on submit and is the authority, so
      // one failed read must not strand someone who is perfectly entitled.
      setResolution(null);
      onBlockedChange(false, selectedProductId);
      onCoveredChange?.(false);
    }
  }, [
    locked,
    meetingTypeId,
    selectedProductId,
    durationMinutes,
    requiresEntitlement,
    onBlockedChange,
    onCoveredChange,
  ]);

  useEffect(() => {
    resolve();
  }, [resolve, isSignedIn]);

  // What they could buy. Fetched once the page is known to be locked, so it is
  // ready the instant they hit the wall.
  useEffect(() => {
    if (!locked || !meetingTypeId) return;
    let cancelled = false;
    customerService
      .getPurchasable(meetingTypeId)
      .then((r) => {
        if (cancelled) return;
        setProducts(r.products);
        setPointCost(r.pointCost ?? null);
        setPurchasableLoaded(true);
      })
      .catch(() => {
        /* non-fatal — the prompt still explains what is needed */
      });
    return () => {
      cancelled = true;
    };
  }, [locked, meetingTypeId]);

  /**
   * Open when the CALLER says they tried to move on and could not.
   *
   * This used to fire on selection instead, which meant picking a service threw
   * up a sign-in dialog before the visitor had done anything that needed one —
   * and dismissing it only armed the same effect again on the next selection.
   * A wall belongs at the point someone walks into it, not at the point they
   * look at the door.
   *
   * `promptToken` is a counter rather than a boolean so a second blocked
   * attempt reopens the dialog after the first was dismissed.
   */
  useEffect(() => {
    if (promptToken > 0) setNeedOpen(true);
  }, [promptToken]);

  /**
   * A page with nothing to choose from stops people at the page itself.
   *
   * Only where the caller asked for it — a service page must NOT do this, or
   * a dialog would greet every visitor before they had looked at the options.
   * `wantsPoints` rather than `blocked` alone, because a plan is settled at the
   * end of the booking and stopping someone up front for one is the bug this
   * component was built to remove.
   */
  const wantsPoints = accessRequirement === 'point' || (pointCost ?? 0) > 0;

  /**
   * The one thing only the host can fix: a points page with no pack to buy
   * points from. Computed once and used by BOTH dialogs below, so they can
   * never both decide to open.
   *
   * `purchasableLoaded` first — an empty `products` before the read lands
   * means "not known", not "nothing on sale".
   */
  const missingPointPack =
    purchasableLoaded &&
    wantsPoints &&
    !products.some((p) => p.kind === 'point_pack' && p.purchasable !== false);

  useEffect(() => {
    if (!autoPromptWhenBlocked || autoPromptedRef.current) return;
    if (!resolution || resolution.allowed || !wantsPoints) return;
    /**
     * ⚠️ THE HOST STILL SEES THE GUEST'S DIALOG. This used to skip the owner
     * outright, to stop two modals stacking — which meant the person most
     * likely to be testing the page saw NOTHING on it at all. The only case
     * that genuinely needs the owner's own dialog instead is a missing point
     * pack, so that, and only that, stands the guest one down.
     */
    if (isPageOwner && missingPointPack) return;
    autoPromptedRef.current = true;
    setNeedOpen(true);
  }, [autoPromptWhenBlocked, resolution, wantsPoints, isPageOwner, missingPointPack]);

  /**
   * ⚠️ THE HOST'S OWN PAGE IS BROKEN AND ONLY THE HOST CAN FIX IT.
   *
   * Priced in points with no pack on sale, this page cannot be booked by
   * anyone: there is nowhere to buy the currency it asks for. The guest-facing
   * copy says "please contact the host", which is worse than useless when the
   * host is the one reading it. So the owner — and only the owner — gets told
   * plainly, on the page where they would notice.
   *
   * Waits for the purchasable read to have happened at all (`pointCost` is set
   * alongside `products`), or an empty list on the first render would accuse a
   * perfectly good page of being broken.
   */
  useEffect(() => {
    if (!isPageOwner || ownerWarnedRef.current) return;
    if (!resolution || !missingPointPack) return;
    ownerWarnedRef.current = true;
    setOwnerWarningOpen(true);
  }, [isPageOwner, resolution, missingPointPack]);

  /**
   * CLOSING THE POP-UP PUTS THE OPTION BACK.
   *
   * Advancing is driven by a CHANGE of selection, so an option left selected
   * behind a dismissed dialog swallows the next tap on it: the visitor closes
   * the pop-up, presses the very option they were just stopped on, and nothing
   * happens — the page looks frozen on the one control they are trying to use.
   * Releasing it makes that tap a real choice again.
   *
   * Only on a real dismissal. Buying a pack and signing in both close this
   * dialog too, and both are ways of getting PAST it.
   */
  const dismissNeed = () => {
    setNeedOpen(false);
    onDismissed?.();
  };

  if (!locked) return null;

  /**
   * Only what a card can actually take.
   *
   * A pack priced under the network floor (¥50, $0.50…) is refused by Stripe at
   * the payment step, so listing it hands the buyer a button that fails after
   * they have chosen, typed their address and reached for a card. The server
   * decides — see `purchasable` on the product.
   */
  const buyable = (p: PurchasableProduct) => p.purchasable !== false;
  const pointPacks = products.filter((p) => p.kind === 'point_pack' && buyable(p));
  /**
   * Offer a sign-in ONLY to someone who is not signed in.
   *
   * `no_contact` means signed in but holding nothing with THIS host, which the
   * server used to report as `not_logged_in` — so an already-signed-in visitor
   * was shown a login form they had no use for and could not get past. Belt
   * and braces on `isSignedIn` too: whatever the server says, never ask
   * someone to sign in twice.
   */
  const needsSignIn = !isSignedIn && resolution?.reason === 'not_logged_in';
  /**
   * A PLAN is what is missing, rather than points.
   *
   * Driven by the chosen option, because the page may accept either: picking
   * the plan option and being told to buy points would be answering a question
   * nobody asked.
   */
  const needsPlan = selectedProductKind === 'subscription';

  /**
   * They are being asked for points and do not have enough.
   *
   * Keyed on the RESOLVED method rather than the page's requirement, so a page
   * that also takes a plan stays quiet for someone whose plan already covers
   * it — the server picks the allowance first, and there is nothing to warn
   * about when it does.
   */
  const shortOnPoints =
    resolution?.method === 'points' &&
    !resolution.allowed &&
    resolution.reason === 'insufficient_points';

  /**
   * Leave for the shop, remembering what they had chosen.
   *
   * The shop is a different page, so without the note they would come back to
   * an empty option list having just been sent away to spend money. The note
   * carries a CHOICE and a return path — never an entitlement, which is
   * re-resolved from the server on the way back.
   */
  const goToPointShop = () => {
    if (!username) return;
    if (meetingTypeId) {
      saveBookingResume({
        meetingTypeId,
        serviceId: selectedProductId,
        returnTo: `${window.location.pathname}${window.location.search}`,
      });
    }
    navigate(
      `/${encodeURIComponent(username)}/shop/points?return=${encodeURIComponent(
        `${window.location.pathname}${window.location.search}`
      )}`
    );
  };

  /** A shop to send them to, with something on its shelf. */
  const canVisitShop = Boolean(username) && pointPacks.length > 0;

  /**
   * The sign-in prompt, and whether this step is a place for it.
   *
   * It belongs where someone is still deciding what to book. Following them
   * into host selection, it sat above a screen that has nothing to do with
   * signing in and invited them to interrupt a choice already made.
   */
  const signInBanner = !isSignedIn && showSignInBanner;
  const pointsBanner = isSignedIn && shortOnPoints;

  /**
   * ⚠️ THERE IS NO "YOU ARE COVERED" BANNER, DELIBERATELY.
   *
   * A green bar confirming a balance or a plan allowance says nothing the page
   * is not already saying: the balance is in the header chip, and a plan's
   * remaining allowance is printed on the option itself, in place of a price.
   * Being able to book is the normal case, and the normal case does not need
   * announcing — only being STOPPED does. Do not add one back.
   */

  /**
   * Nothing to say. Rendered as NOTHING rather than an empty padded box —
   * the caller supplies this block's padding, so a bare wrapper would leave a
   * band of blank space above the options on every page that is fine.
   */
  const hasBanner = signInBanner || pointsBanner;

  return (
    <>
      {hasBanner && (
        <div className={bannerClassName}>
          {/* Sign-in lives on the first step, not only in the header — a
              returning customer should not have to hunt for it before choosing
              anything. */}
          {signInBanner && (
            <div className="mb-4 flex flex-col gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-secondary-800">
                {t(
                  'booking:guard.returningPrompt',
                  'Already have points or a plan with us?'
                )}
              </p>
              <button
                type="button"
                className="btn-primary btn-sm self-start sm:self-auto"
                onClick={() => setLoginOpen(true)}
              >
                {t('booking:customer.signIn', 'Sign in')}
              </button>
            </div>
          )}

          {/* Short on points, said ABOVE the options rather than only when they
              try to move on. Someone browsing services priced in points needs
              to know they cannot afford any of them BEFORE picking one —
              discovering it at the Continue button is a wasted decision. */}
          {pointsBanner && (
            <div className="mb-4 flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-amber-900">
                {t('booking:guard.pointsShort', 'You have {{balance}} points.', {
                  balance: resolution?.pointBalance ?? 0,
                })}
              </p>
              <button
                type="button"
                className="btn-primary btn-sm self-start sm:self-auto"
                onClick={() => setNeedOpen(true)}
                disabled={pointPacks.length === 0}
              >
                {t('booking:guard.buyPoints', 'Buy points')}
              </button>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        title={t('booking:customer.signInTitle', 'Sign in')}
        size="small"
      >
        <CustomerLoginForm
          onSignedIn={async () => {
            setLoginOpen(false);
            await refresh();
            await resolve();
            onIdentityChanged?.();
          }}
          onCancel={() => setLoginOpen(false)}
        />
      </Modal>

      {/* Hit when they try to move on without what this booking needs. */}
      <Modal
        isOpen={needOpen}
        onClose={dismissNeed}
        title={
          needsSignIn
            ? t('booking:guard.pointsNeededSignedOutTitle', 'This booking is paid with points')
            : needsPlan
              ? t('booking:guard.planNeededTitle', 'This option needs a plan')
              : /**
                 * ⚠️ ONLY CALL IT A SHORTAGE WHEN IT IS ONE.
                 *
                 * This was the fallback for every refusal that was not a
                 * sign-in or a plan, so a page that simply could not be priced
                 * announced "you need more points" to someone holding
                 * thousands. The server now names that case; anything else
                 * unrecognised gets the neutral title rather than a guess
                 * about the reader's balance.
                 */
                shortOnPoints
                ? t('booking:guard.pointsNeededTitle', 'You need more points')
                : t('booking:guard.unavailableTitle', 'This cannot be booked right now')
        }
        size="small"
      >
        {needsSignIn ? (
          /**
           * TWO DOORS, NOT ONE.
           *
           * This showed only the sign-in form, which quietly assumed everyone
           * who lands on a points page already has an account — so a first-time
           * visitor was handed a password field for a password they had never
           * set, with no way to buy the points the page was asking for.
           *
           * Buying comes first because it is what most people here need; the
           * sign-in sits underneath for the ones coming back.
           */
          <div className="space-y-5">
            {/* THE COIN SAYS IT BEFORE THE SENTENCE DOES.
                "Paid for with points" is an unusual thing to read on a booking
                page, and a wall of text is the slowest way to learn it. The
                same coin as the purchase receipt, so the thing being asked for
                and the thing received look like one object. */}
            <div className="flex justify-center">
              <CoinMark size={56} />
            </div>

            <p className="text-center text-sm text-gray-600">
              {t(
                'booking:guard.pointsNeededSignedOutBody',
                'Bookings on this page are paid for with points. Buy some to continue, or sign in if you already have them.'
              )}
            </p>

            <div className="space-y-2">
              <button
                type="button"
                className="btn-primary w-full"
                onClick={goToPointShop}
                disabled={!canVisitShop}
              >
                {t('booking:guard.buyPoints', 'Buy points')}
              </button>
              {!canVisitShop && (
                <p className="text-xs text-amber-600">
                  {t(
                    'booking:guard.noPacksOnSale',
                    'There are no point packs on sale right now — please contact the host.'
                  )}
                </p>
              )}
              {canVisitShop && (
                <p className="text-center text-xs text-gray-500">
                  {t(
                    'booking:guard.buyPointsHint',
                    "We'll bring you back here when you're done."
                  )}
                </p>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4">
              <p className="mb-3 text-sm font-medium text-gray-700">
                {t('booking:guard.alreadyHavePoints', 'Already have points?')}
              </p>
              <CustomerLoginForm
                onSignedIn={async () => {
                  // Straight back to the option list, now knowing what they
                  // hold. Nothing else moves — they choose from here.
                  setNeedOpen(false);
                  await refresh();
                  await resolve();
                  onIdentityChanged?.();
                }}
                onCancel={dismissNeed}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {/* Neutral, and deliberately free of any mention of points or
                  plans: the host has not been released the system, and naming
                  it to their invitee would advertise something neither of them
                  can act on. */}
              {resolution?.reason === 'feature_unavailable' ||
              /* The host priced this page in points but set no price for what
                 is being booked. Nothing the guest can do, and nothing their
                 balance has to do with — so it must never read as a shortage. */
              resolution?.reason === 'unpriced'
                ? t(
                    'booking:guard.unavailableBody',
                    'This option cannot be booked at the moment. Please contact the host.'
                  )
                : resolution?.reason === 'allowance_exhausted'
                ? t(
                    'booking:guard.allowanceUsedBody',
                    'You have used all the meetings your plan includes for this period.'
                  )
                : needsPlan
                  ? t(
                      'booking:guard.planNeededBody',
                      // No button under it, and none belongs there: the plan is
                      // joined on the last screen of the booking, not here.
                      'This option is included in a plan rather than sold on its own. Carry on choosing a time — you can join the plan at the last step.'
                    )
                  : resolution?.reason === 'no_contact'
                    ? /* Signed in, but nothing with THIS host yet. Quoting a
                         cost of 0 and a balance of 0 at them, as the generic
                         line below would, describes nothing they can act on. */
                      t(
                        'booking:guard.noWalletHereBody',
                        "You don't have any points with this host yet."
                      )
                    : shortOnPoints
                      ? t(
                          'booking:guard.pointsNeededBody',
                          'This booking costs {{cost}} points and you have {{balance}}.',
                          {
                            cost: resolution?.pointCost ?? 0,
                            balance: resolution?.pointBalance ?? 0,
                          }
                        )
                      : /* Refused for a reason we have no words for. Say that,
                           rather than quoting numbers at someone whose balance
                           was never the problem. */
                        t(
                          'booking:guard.unavailableBody',
                          'This option cannot be booked at the moment. Please contact the host.'
                        )}
            </p>

            {/* NO PLAN IS SOLD FROM THIS DIALOG.
                A plan is joined at the END of the booking, on the checkout the
                server asks for once it sees the plan is not theirs yet — and
                buying one here would start a SECOND subscription for anyone
                who already holds a different plan. The plan options are on the
                page behind this dialog; picking one is how you choose it. */}

            {!needsPlan && shortOnPoints && pointPacks.length > 0 && (
              <div className="space-y-2">
                {pointPacks.map((pack) => (
                  <button
                    key={pack.id}
                    type="button"
                    onClick={() => {
                      setNeedOpen(false);
                      setBuying(pack);
                    }}
                    className="flex w-full items-center gap-3 rounded-md border border-gray-200 px-3 py-2.5 text-left hover:border-primary-400"
                  >
                    {/* The pack's own imagery, resolved by the same ladder the
                        option list uses. A shelf that shows a picture and a
                        dialog that shows a bare title are the same product
                        looking like two. */}
                    <ProductThumbnail product={pack} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {pack.title}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {t('booking:points.packSize', '{{count}} points', {
                          count: pack.pointsGranted ?? 0,
                        })}
                      </span>
                    </span>
                    {pack.price != null && (
                      <span className="ml-3 flex-shrink-0 text-sm font-medium text-gray-900">
                        {formatCurrency(Number(pack.price), pack.currency)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* SAY WHAT IS ACTUALLY MISSING.
                Keyed on the PACKS, not on `products` — that list also holds the
                page's plans, so a host with plans but no sellable pack showed a
                dialog with a message, no packs and no note: a dead end that
                looked like a loading bug. */}
            {!needsPlan && shortOnPoints && pointPacks.length === 0 && (
              <p className="text-xs text-amber-600">
                {t(
                  'booking:guard.noPacksOnSale',
                  'There are no point packs on sale right now — please contact the host.'
                )}
              </p>
            )}

            <div className="flex justify-end pt-2">
              <button type="button" className="btn-secondary" onClick={dismissNeed}>
                {t('common:actions.close', 'Close')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* HOST ONLY. Never rendered for a guest — `isPageOwner` is the signed-in
          host looking at their own link, and this says the one thing the
          guest-facing copy cannot: which of THEIR settings is missing. */}
      <Modal
        isOpen={ownerWarningOpen}
        onClose={() => setOwnerWarningOpen(false)}
        title={t('booking:owner.noPointPackTitle', 'Guests cannot book this page yet')}
        size="small"
      >
        <div className="space-y-4">
          <div className="flex justify-center">
            <CoinMark size={56} />
          </div>
          <p className="text-sm text-gray-600">
            {t(
              'booking:owner.noPointPackBody',
              'This page is paid for in points, but you have no point pack on sale — so there is nowhere for a guest to buy the points it asks for. Create one and this page starts working.'
            )}
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setOwnerWarningOpen(false)}
            >
              {t('common:actions.close', 'Close')}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/products?kind=point_pack')}
            >
              {t('booking:owner.createPointPack', 'Create a point pack')}
            </button>
          </div>
        </div>
      </Modal>

      <PointPurchaseModal
        isOpen={buying !== null}
        product={buying}
        onClose={() => setBuying(null)}
        onPurchased={async () => {
          setBuying(null);
          await refresh();
          // They may be able to continue now.
          await resolve();
        }}
      />
    </>
  );
};

export default ServiceEntitlementGuard;
